import { useCallback, useContext, useMemo, useSyncExternalStore } from 'react';

import { ProjectCtx } from '@/hooks/project-context';
import { apiClient } from '@/lib/api';

type ProjectCapability = 'analyses' | 'verify' | 'tsa_workspace';

/**
 * Project-scoped capability flags.
 *
 * Capability adoption is persisted on the project by the API. The announcement
 * booleans are local UI acknowledgement state only; they never decide whether a
 * lane exists.
 */
interface ProjectCapabilities {
  readonly analyses: boolean;
  readonly verify: boolean;
  readonly tsa_workspace: boolean;
  readonly analysesAnnounced: boolean;
  readonly verifyAnnounced: boolean;
  readonly tsaWorkspaceAnnounced: boolean;
}

const DEFAULT_ANNOUNCEMENTS = {
  analysesAnnounced: false,
  verifyAnnounced: false,
  tsaWorkspaceAnnounced: false,
};

const DEFAULT_CAPABILITIES: ProjectCapabilities = {
  analyses: false,
  verify: false,
  tsa_workspace: false,
  ...DEFAULT_ANNOUNCEMENTS,
};

const STORAGE_PREFIX = 'cav2:project:';
const STORAGE_SUFFIX = ':capability-announcements';
const UPDATE_EVENT = 'cav2:project-capability-announcements-changed';

function storageKey(projectId: string): string {
  return `${STORAGE_PREFIX}${projectId}${STORAGE_SUFFIX}`;
}

function readAnnouncements(projectId: string): typeof DEFAULT_ANNOUNCEMENTS {
  try {
    const raw = localStorage.getItem(storageKey(projectId));
    if (raw === null) {
      return DEFAULT_ANNOUNCEMENTS;
    }
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) {
      return DEFAULT_ANNOUNCEMENTS;
    }
    return {
      analysesAnnounced: Reflect.get(parsed, 'analysesAnnounced') === true,
      verifyAnnounced: Reflect.get(parsed, 'verifyAnnounced') === true,
      tsaWorkspaceAnnounced: Reflect.get(parsed, 'tsaWorkspaceAnnounced') === true,
    };
  } catch {
    return DEFAULT_ANNOUNCEMENTS;
  }
}

function writeAnnouncements(
  projectId: string,
  announcements: typeof DEFAULT_ANNOUNCEMENTS,
): void {
  try {
    localStorage.setItem(storageKey(projectId), JSON.stringify(announcements));
  } catch {
    // Announcement state is cosmetic; blocked storage should not break the app.
  }
  window.dispatchEvent(new Event(UPDATE_EVENT));
}

const snapshotCache = new Map<
  string,
  { raw: string | null; value: typeof DEFAULT_ANNOUNCEMENTS }
>();

function readCachedAnnouncements(projectId: string): typeof DEFAULT_ANNOUNCEMENTS {
  let raw: string | null;
  try {
    raw = localStorage.getItem(storageKey(projectId));
  } catch {
    raw = null;
  }
  const cached = snapshotCache.get(projectId);
  if (cached !== undefined && cached.raw === raw) {
    return cached.value;
  }
  const value = readAnnouncements(projectId);
  snapshotCache.set(projectId, { raw, value });
  return value;
}

function getServerSnapshot(): typeof DEFAULT_ANNOUNCEMENTS {
  return DEFAULT_ANNOUNCEMENTS;
}

interface UseProjectCapabilitiesResult {
  readonly capabilities: ProjectCapabilities;
  readonly adopt: (capability: ProjectCapability) => Promise<void>;
  readonly disable: (capability: ProjectCapability) => Promise<void>;
  readonly markAnnounced: (capability: ProjectCapability) => void;
}

export function useProjectCapabilities(
  projectId: string | null,
): UseProjectCapabilitiesResult {
  const projectContext = useContext(ProjectCtx);
  const project =
    projectId !== null && projectContext?.project.project_id === projectId
      ? projectContext.project
      : null;
  const updateProject = projectContext?.updateProject;

  const subscribe = useCallback(
    (onChange: () => void) => {
      if (projectId === null) {
        return () => undefined;
      }
      const onStorage = (event: StorageEvent): void => {
        if (event.key === storageKey(projectId)) {
          onChange();
        }
      };
      window.addEventListener(UPDATE_EVENT, onChange);
      window.addEventListener('storage', onStorage);
      return () => {
        window.removeEventListener(UPDATE_EVENT, onChange);
        window.removeEventListener('storage', onStorage);
      };
    },
    [projectId],
  );

  const getSnapshot = useCallback((): typeof DEFAULT_ANNOUNCEMENTS => {
    if (projectId === null) {
      return DEFAULT_ANNOUNCEMENTS;
    }
    return readCachedAnnouncements(projectId);
  }, [projectId]);

  const announcements = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const capabilities = useMemo<ProjectCapabilities>(() => {
    if (project === null) {
      return DEFAULT_CAPABILITIES;
    }

    return {
      analyses: project.capabilities?.analyses_enabled ?? false,
      verify: project.capabilities?.verify_enabled ?? false,
      tsa_workspace: project.capabilities?.tsa_enabled ?? false,
      ...announcements,
    };
  }, [announcements, project]);

  const updateRemoteCapability = useCallback(
    async (capability: ProjectCapability, enabled: boolean): Promise<void> => {
      if (projectId === null) {
        return;
      }

      const updatedProject =
        capability === 'analyses' && enabled
          ? await apiClient.adoptProjectAnalysesCapability(projectId)
          : await apiClient.updateProjectCapabilities(
              projectId,
              capabilityPatch(capability, enabled),
            );

      updateProject?.(updatedProject);

      if (!enabled) {
        const current = readAnnouncements(projectId);
        writeAnnouncements(projectId, withAnnouncement(current, capability, false));
      }
    },
    [projectId, updateProject],
  );

  const adopt = useCallback(
    (capability: ProjectCapability): Promise<void> => updateRemoteCapability(capability, true),
    [updateRemoteCapability],
  );

  const disable = useCallback(
    (capability: ProjectCapability): Promise<void> => updateRemoteCapability(capability, false),
    [updateRemoteCapability],
  );

  const markAnnounced = useCallback(
    (capability: ProjectCapability) => {
      if (projectId === null) {
        return;
      }
      const current = readAnnouncements(projectId);
      writeAnnouncements(projectId, withAnnouncement(current, capability, true));
    },
    [projectId],
  );

  return { adopt, capabilities, disable, markAnnounced };
}

function capabilityPatch(
  capability: ProjectCapability,
  enabled: boolean,
): Parameters<typeof apiClient.updateProjectCapabilities>[1] {
  switch (capability) {
    case 'analyses':
      return { analyses_enabled: enabled };
    case 'verify':
      return { verify_enabled: enabled };
    case 'tsa_workspace':
      return { tsa_enabled: enabled };
  }
}

function withAnnouncement(
  current: typeof DEFAULT_ANNOUNCEMENTS,
  capability: ProjectCapability,
  announced: boolean,
): typeof DEFAULT_ANNOUNCEMENTS {
  switch (capability) {
    case 'analyses':
      return { ...current, analysesAnnounced: announced };
    case 'verify':
      return { ...current, verifyAnnounced: announced };
    case 'tsa_workspace':
      return { ...current, tsaWorkspaceAnnounced: announced };
  }
}
